import React, { useMemo, useState } from "react";

import { PendingCoverCropSuggestion } from "services";

import { useTranslation } from "utils";

import { FieldListField } from "models";

import { AlertBanner, Flex, Typography } from "components";

import { CoverCropSuggestionCard } from "pages/app/all-field-view/components/tasksDrawer/CoverCropSuggestionCard";
import { DraftDecision, DraftsByDetectionId, isDraftComplete } from "pages/app/all-field-view/components/tasksDrawer/coverCropDetectionUtils";

type CoverCropWizardStepProps = {
  suggestions: PendingCoverCropSuggestion[];
  seasonYears: number[];
  drafts: DraftsByDetectionId;
  fieldsById: Map<string, FieldListField>;
  onChange: (detectionId: string, changes: Partial<DraftDecision>) => void;
};

export const CoverCropWizardStep = ({ suggestions, seasonYears, drafts, fieldsById, onChange }: CoverCropWizardStepProps) => {
  const { t } = useTranslation("allFieldView", { useSuspense: false });

  const [selectedYear, setSelectedYear] = useState<number | undefined>(seasonYears[0]);

  const [openByYear, setOpenByYear] = useState<Record<number, string[]>>({});

  const activeYear = selectedYear !== undefined && seasonYears.includes(selectedYear) ? selectedYear : seasonYears[0];

  const suggestionsForYear = useMemo(() => suggestions.filter((suggestion) => suggestion.seasonYear === activeYear), [suggestions, activeYear]);

  const defaultOpenIds = suggestionsForYear[0] ? [suggestionsForYear[0].detectionId] : [];
  const openIds = openByYear[activeYear] ?? defaultOpenIds;

  const toggleExpanded = (detectionId: string) =>
    setOpenByYear((current) => {
      const open = current[activeYear] ?? defaultOpenIds;

      return { ...current, [activeYear]: open.includes(detectionId) ? open.filter((id) => id !== detectionId) : [...open, detectionId] };
    });

  const yearsWithOutstanding = useMemo(
    () => new Set(suggestions.filter((suggestion) => !isDraftComplete(drafts[suggestion.detectionId])).map(({ seasonYear }) => seasonYear)),
    [suggestions, drafts]
  );

  return (
    <Flex vertical gap="1.5rem" data-testid="cover-crop-wizard-step">
      <Flex vertical gap="1rem">
        <Typography variant="h3" className="margin-0">
          {t("coverCropDetectionDrawer.title")}
        </Typography>
        <Typography variant="paragraph-m" className="margin-0">
          {t("coverCropDetectionDrawer.description")}
        </Typography>
      </Flex>

      <AlertBanner type="info-highlighted" iconName="tabler/satellite" iconAlign="start" description={t("coverCropDetectionDrawer.signalBanner")} />

      <div className="cover-crop-year-tabs" data-testid="cover-crop-year-tabs">
        {seasonYears.map((year) => (
          <div
            key={year}
            role="tab"
            className={`cover-crop-year-tab${activeYear === year ? " active" : ""}`}
            onClick={() => setSelectedYear(year)}
            data-testid={`cover-crop-year-tab-${year}`}
          >
            {year}
            {yearsWithOutstanding.has(year) && <span className="cover-crop-year-tab-dot" />}
          </div>
        ))}
      </div>

      <Flex vertical gap="1rem">
        {suggestionsForYear.map((suggestion) => {
          const field = fieldsById.get(suggestion.fieldId);

          return (
            <CoverCropSuggestionCard
              key={suggestion.detectionId}
              suggestion={suggestion}
              draft={drafts[suggestion.detectionId]}
              fieldLocation={field?.location}
              fieldSize={field?.size}
              isExpanded={openIds.includes(suggestion.detectionId)}
              onToggleExpanded={() => toggleExpanded(suggestion.detectionId)}
              onChange={(changes) => onChange(suggestion.detectionId, changes)}
            />
          );
        })}
      </Flex>
    </Flex>
  );
};
